import * as React from "react"
import * as Antd from "antd"
import styles from "./ResetPasswordForm.module.scss"

import { AppConfig } from "@constants/AppConfig.ts"
import { AppRoutes } from "@constants/AppRoutes.ts"
import { useNavigate } from "react-router-dom"
import { AppFunctions } from "@others/AppFunctions.ts"
import { useTranslation } from "react-i18next"
import { AppNotification } from "@others/AppNotification.ts"
import { IGetErrorApiDetail } from "@interfaces/IGetErrorApiDetail.ts"

import DvInput from "@components/dataEntry/DvInput/DvInput.tsx"
import AppText from "@components/general/AppText/AppText.tsx"
import DsButton from "@components/general/DsButton/DsButton.tsx"
import DvAntdForm from "@components/others/DvAntdForm/DvAntdForm.tsx"
import authService from "@services/authService/authService.ts"
import DvAntdFormItem from "@components/others/DvAntdFormItem/DvAntdFormItem.tsx"

export interface ILoginFormFieldType {
	new_password: string
	confirm_password: string
}

interface IProps {
	className?: string
}

let change_password_token: string | undefined

const ResetPasswordForm: React.FC<IProps> = (props) => {
	const { t } = useTranslation()

	const navigate = useNavigate()

	if (!change_password_token)
		change_password_token =
			AppFunctions.getUrlParam("change-password-token") || undefined

	const [form] = Antd.Form.useForm<ILoginFormFieldType>()
	const [loginLoading, setLoginLoading] = React.useState(false)

	const onFinish = ({
		new_password,
		confirm_password,
	}: ILoginFormFieldType) => {
		setLoginLoading(true)

		if (new_password !== confirm_password) {
			form.setFields([
				{
					name: "confirm_password",
					errors: [t("the_password_does_not_match")],
				},
			])

			setLoginLoading(false)

			return
		}

		authService
			.resetPassword({
				new_password,
				confirm_password,
				change_password_token,
			})
			.then((response) => {
				AppNotification.success({
					message: t("message_password_changes_successfully"),
				})

				navigate(AppRoutes.login, {
					state: {
						fromResetPasswordEmailValue: response?.email,
					},
				})
			})
			.catch(({ message, code }: IGetErrorApiDetail) => {
				switch (code) {
					case "NEW_PASSWORD_REQUIRED":
						form.setFields([
							{
								name: "new_password",
								errors: [t("please_enter_your_new_password")],
							},
						])
						break
					case "CONFIRM_PASSWORD_REQUIRED":
						form.setFields([
							{
								name: "confirm_password",
								errors: [t("please_confirm_your_new_password")],
							},
						])
						break
					case "PASSWORD_MISMATCH":
						form.setFields([
							{
								name: "confirm_password",
								errors: [t("the_password_does_not_match")],
							},
						])
						break
					default:
						if (code === "INVALID_OR_EXPIRED_TOKEN") {
							navigate(AppRoutes.requestResetPassword, {
								replace: true,
							})
						}

						AppNotification.error({
							message: message || t("something_went_wrong"),
						})
				}
			})
			.finally(() => {
				setLoginLoading(false)
			})
	}

	React.useEffect(() => {
		if (change_password_token) {
			navigate(AppRoutes.resetPassword, { replace: true })

			if (AppFunctions.isAMobileDevice()) {
				window.location.href = `${AppConfig.RESET_PASSWORD_MOBILE_APP_URL}?changePasswordToken=${change_password_token}`
			}
		} else {
			navigate(AppRoutes.notFound, { replace: true })
		}
	}, [])

	return (
		<DvAntdForm<ILoginFormFieldType>
			form={form}
			className={`${styles.login_form} ${props.className || ""}`}
			onFinish={onFinish}
		>
			<div className={styles.top}>
				<AppText
					className={styles.header}
					textKey={"please_enter_your_new_password"}
				/>

				<main className={styles.body}>
					<DvAntdFormItem<ILoginFormFieldType>
						label={t("new_password")}
						name="new_password"
						rules={[
							{
								required: true,
								message: t("please_enter_your_password"),
							},
						]}
					>
						<DvInput.Password
							placeholder={t("enter_your_new_password")}
						/>
					</DvAntdFormItem>

					<DvAntdFormItem<ILoginFormFieldType>
						label={t("confirm_password")}
						name="confirm_password"
						rules={[
							{
								required: true,
								message: t("please_confirm_your_new_password"),
							},
						]}
					>
						<DvInput.Password
							placeholder={t("confirm_your_new_password")}
						/>
					</DvAntdFormItem>
				</main>
			</div>

			<div className={styles.footer}>
				<DsButton
					className={styles.action_buttons}
					htmlType="submit"
					textKey="send"
					loading={loginLoading}
				/>
			</div>
		</DvAntdForm>
	)
}

export default React.memo(ResetPasswordForm)
